<template>
  <div ref="chartRef" class="bar-chart" :style="{ height: normalizedHeight }"></div>
</template>

<script setup>
/* global defineProps */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  categories: { type: Array, default: () => [] },
  values: { type: Array, default: () => [] },
  series: { type: Array, default: () => [] },
  colors: { type: Array, default: () => [] },
  horizontal: { type: Boolean, default: false },
  ySplitNumber: { type: Number, default: 4 },
  barWidth: { type: [Number, String], default: 56 },
  barCategoryGap: { type: [Number, String], default: '20%' },
  xAxisLabelFontSize: { type: Number, default: 12 },
  xAxisLabelRotate: { type: Number, default: 0 },
  gridBottom: { type: [Number, String], default: 40 },
  height: { type: [Number, String], default: 300 }
});

const normalizedHeight = typeof props.height === 'number' ? `${props.height}px` : props.height;

const chartRef = ref(null);
let chartInstance = null;

const defaultPalette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']; // 兜底配色
const getColor = (idx) => props.colors[idx] || defaultPalette[idx % defaultPalette.length];

const buildOption = () => {
  // categories 与 values 一一对应（单序列模式）
  // 或 categories 与 series[*].values 对应（多序列分组柱模式）
  const cats = props.categories || [];
  const vals = props.values || [];
  const groupedSeries = Array.isArray(props.series) ? props.series : [];
  const hasGroupedSeries = groupedSeries.length > 0;

  const chartSeries = hasGroupedSeries
    ? groupedSeries.map((item, idx) => ({
      name: item?.name || `系列${idx + 1}`,
      type: 'bar',
      data: Array.isArray(item?.values) ? item.values : [],
      barWidth: item?.barWidth || props.barWidth,
      barCategoryGap: props.barCategoryGap,
      itemStyle: {
        color: item?.color || getColor(idx)
      },
      emphasis: { focus: 'series' }
    }))
    : [
      {
        type: 'bar',
        data: vals,
        barWidth: props.barWidth,
        barCategoryGap: props.barCategoryGap,
        itemStyle: {
          color: (params) => getColor(params.dataIndex) // 每根柱子可独立着色
        },
        emphasis: {
          focus: 'series'
        }
      }
    ];

  const xAxisOption = props.horizontal
    ? {
      type: 'value',
      splitNumber: props.ySplitNumber,
      axisLine: { show: true, lineStyle: { color: '#9ca3af' } },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { type: 'dashed' } }
    }
    : {
      type: 'category',
      data: cats,
      axisLine: { lineStyle: { color: '#9ca3af' } },
      axisTick: { alignWithLabel: true },
      axisLabel: {
        interval: 0,
        rotate: props.xAxisLabelRotate,
        fontSize: props.xAxisLabelFontSize,
        color: '#475569'
      },
      splitLine: { show: true, lineStyle: { type: 'dashed' } }
    };

  const yAxisOption = props.horizontal
    ? {
      type: 'category',
      data: cats,
      axisLine: { lineStyle: { color: '#9ca3af' } },
      axisTick: { alignWithLabel: true },
      axisLabel: {
        interval: 0,
        fontSize: props.xAxisLabelFontSize,
        color: '#475569'
      },
      splitLine: { show: false }
    }
    : {
      type: 'value',
      splitNumber: props.ySplitNumber,
      axisLine: { show: true, lineStyle: { color: '#9ca3af' } },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { type: 'dashed' } }
    };

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    legend: hasGroupedSeries
      ? {
        bottom: 0,
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: '#475569', fontSize: 12 }
      }
      : undefined,
    grid: props.horizontal
      ? { left: 64, right: 20, top: 20, bottom: props.gridBottom }
      : { left: 40, right: 20, top: 20, bottom: props.gridBottom },
    xAxis: xAxisOption,
    yAxis: yAxisOption,
    series: chartSeries
  };
};

const renderChart = () => {
  if (!chartRef.value) return;
  if (!chartInstance) chartInstance = echarts.init(chartRef.value);
  chartInstance.setOption(buildOption(), true);
};

const resizeChart = () => { if (chartInstance) chartInstance.resize(); };

onMounted(() => {
  renderChart();
  window.addEventListener('resize', resizeChart);
});

watch(
  () => [props.categories, props.values, props.series, props.colors, props.horizontal, props.ySplitNumber, props.barWidth, props.barCategoryGap, props.xAxisLabelFontSize, props.xAxisLabelRotate, props.gridBottom, props.height],
  () => renderChart(),
  { deep: true }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeChart);
  if (chartInstance) { chartInstance.dispose(); chartInstance = null; }
});


</script>

<style scoped>
.bar-chart {
  width: 100%;
}
</style>
